import { For, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import {
  BLACK_OPENINGS,
  filterOpeningsByMoves,
  WHITE_OPENINGS,
  type OpeningMoveFilter,
} from '@domain/openings/openings';
import {
  developedPieces,
  nextOpeningQuiz,
  positionsMatch,
  type DevelopedPiece,
} from '@domain/openings/quiz';
import { squareShade } from '@domain/chess/fen';
import { recordModeAttempt } from '@state/modeStore';
import type { Opening } from '@domain/openings/openings';
import {
  loadOpening,
  newGame,
  setOrientation,
  setRevealCorrectSquares,
  snapshotPieces,
} from '@state/actions';
import { gameStore } from '@state/gameStore';
import { GameControls } from '@ui/Controls/GameControls';
import { pieceUrl } from '@ui/Board/pieceUrls';
import {
  OpeningInsight,
  spokenOpeningNotes,
} from '@ui/Openings/OpeningInsight';
import { OpeningSearch } from '@ui/Openings/OpeningSearch';
import { speakParagraphs, stopSpeech } from '@ui/speech/speak';
import { TurnStack } from '@ui/TurnStack/TurnStack';
import './OpeningQuiz.css';

const CATALOG = [...WHITE_OPENINGS, ...BLACK_OPENINGS];

export function OpeningQuiz() {
  const [item, setItem] = createSignal(
    nextOpeningQuiz(filterOpeningsByMoves(CATALOG, '1')),
  );
  const [revealed, setRevealed] = createSignal(false);
  const [lastResult, setLastResult] = createSignal<'won' | 'lost' | null>(null);
  const [moveFilter, setMoveFilter] = createSignal<OpeningMoveFilter>('1');
  const [reading, setReading] = createSignal<number | null>(null);
  const filteredCatalog = () => filterOpeningsByMoves(CATALOG, moveFilter());
  const listening = () => reading() !== null;

  function haltSpeech() {
    stopSpeech();
    setReading(null);
  }

  function present(next: ReturnType<typeof nextOpeningQuiz>) {
    haltSpeech();
    setItem(next);
    setRevealed(false);
    setLastResult(null);
    newGame();
    setOrientation(next.opening.side);
  }

  const turnRows = createMemo(() => {
    const rows = new Map<number, { white: DevelopedPiece[]; black: DevelopedPiece[] }>();
    for (const piece of item().pieces) {
      const steps =
        piece.path.length > 0
          ? piece.path
          : [
              {
                turn: piece.turn ?? 1,
                square: piece.square,
                shade: piece.shade,
              },
            ];
      for (const step of steps) {
        const row = rows.get(step.turn) ?? { white: [], black: [] };
        row[piece.color === 'w' ? 'white' : 'black'].push({
          ...piece,
          square: step.square,
          shade: step.shade ?? squareShade(step.square),
          turn: step.turn,
        });
        rows.set(step.turn, row);
      }
    }
    return [...rows.entries()].sort(([a], [b]) => a - b);
  });

  onMount(() => {
    setOrientation(item().opening.side);
  });

  onCleanup(() => {
    haltSpeech();
  });

  function onListen() {
    if (listening()) {
      haltSpeech();
      return;
    }
    let started = false;
    setReading(0);
    speakParagraphs(spokenOpeningNotes(item().opening.name), (index) => {
      if (index !== null) {
        started = true;
        setReading(index);
        return;
      }
      if (started) setReading(null);
    });
  }

  function onShow() {
    const opening = item().opening;
    const matched = positionsMatch(gameStore.fen, opening.fen);
    const beforeReveal = snapshotPieces();
    const correctSquares = developedPieces(opening.fen, opening.moves)
      .filter((piece) => !piece.captured)
      .filter((piece) => {
        const placed = beforeReveal.pieces.get(piece.square);
        return placed?.color === piece.color && placed.role === piece.role;
      })
      .map((piece) => piece.square);
    recordModeAttempt('opening', matched);
    loadOpening(opening);
    setRevealCorrectSquares(correctSquares);
    setLastResult(matched ? 'won' : 'lost');
    setRevealed(true);
  }

  function onPickSearch(opening: Opening) {
    present({
      opening,
      pieces: developedPieces(opening.fen, opening.moves),
    });
  }

  function onNext() {
    present(nextOpeningQuiz(filteredCatalog(), { excludeName: item().opening.name }));
  }

  function resetQuiz() {
    haltSpeech();
    setRevealed(false);
    setLastResult(null);
    newGame();
    setOrientation(item().opening.side);
  }

  return (
    <>
      <section class="game-rules" aria-label="Game rules">
        <p>
          Recreate the named opening on the board by using the hints below, then reveal the solution.
          After revealing it, you can continue playing with basic analysis enabled or start a new
          opening puzzle. Progress for every mode is on the heatmaps at the bottom of the
          page.
        </p>
      </section>
      <GameControls onReset={resetQuiz} />
      <h2 class="opening-quiz__title">{item().opening.name}</h2>
      <div class="opening-quiz__toolbar">
        <select
          id="opening-move-filter"
          class="opening-search__moves opening-quiz__select"
          aria-label="Filter openings by moves"
          value={moveFilter()}
          onChange={(e) => {
            const filter = e.currentTarget.value as OpeningMoveFilter;
            setMoveFilter(filter);
            present(
              nextOpeningQuiz(filterOpeningsByMoves(CATALOG, filter), {
                excludeName: item().opening.name,
              }),
            );
          }}
        >
          <option value="1">1 move</option>
          <option value="2">2 moves</option>
          <option value="3">3 moves</option>
          <option value="4">4 moves</option>
          <option value="more">More</option>
          <option value="all">All</option>
        </select>
        <OpeningSearch onPick={onPickSearch} moveFilter={moveFilter()} />
        <button
          type="button"
          class="btn opening-quiz__reveal"
          onClick={() => (revealed() ? onNext() : onShow())}
        >
          {revealed() ? 'Next' : 'Reveal'}
        </button>
        <Show when={revealed()}>
          <button
            type="button"
            class="btn opening-quiz__listen"
            aria-pressed={listening()}
            title={listening() ? 'Stop reading' : 'Listen to notes'}
            onClick={onListen}
          >
            {listening() ? 'Stop' : 'Listen'}
          </button>
        </Show>
      </div>
      <div class="opening-quiz-stage">
        <Show when={!revealed()} fallback={<OpeningInsight reading={reading()} />}>
          <section class="opening-quiz" aria-label="Opening quiz">
            <h3 class="opening-quiz__sequence">Move sequence</h3>
            <div class="opening-quiz__squares">
              <For each={turnRows()}>
                {([turn, row]) => (
                  <div class="opening-quiz__turn-row">
                    <div class="opening-quiz__turn-side opening-quiz__turn-side--white">
                      <For each={row.white}>
                        {(piece) => <MovedSquare piece={piece} turn={turn} />}
                      </For>
                    </div>
                    <div class="opening-quiz__turn-side opening-quiz__turn-side--black">
                      <For each={row.black}>
                        {(piece) => <MovedSquare piece={piece} turn={turn} />}
                      </For>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </section>
        </Show>
      </div>
    </>
  );
}

function MovedSquare(props: { piece: DevelopedPiece; turn?: number }) {
  const p = () => props.piece;
  return (
    <div
      class="quiz-sq"
      classList={{
        'quiz-sq--light': p().shade === 'light',
        'quiz-sq--dark': p().shade === 'dark',
      }}
    >
      <Show when={props.turn !== undefined}>
        <TurnStack
          turns={
            p().turns.filter((turn) => turn <= props.turn!).length > 0
              ? p().turns.filter((turn) => turn <= props.turn!)
              : [props.turn!]
          }
        />
      </Show>
      <img src={pieceUrl(p().color, p().role)} alt="" draggable={false} />
    </div>
  );
}
